import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { logoutUser, type LogoutReason } from '@/utils/auth';

import { electronApis } from '../electron-api';

import { waitForPostProcessingFinalization } from './meetings.post-processing';
import { meetingApis } from './meetings.query';
import { meetingStore } from './meetings.state';

type MeetingGuardAction = 'quit' | 'logout';
type MeetingGuardResult = 'allowed' | 'blocked';

let activeMeetingGuardConfirmation: Promise<boolean> | null = null;
let activeMeetingStopRequest: Promise<void> | null = null;

const MEETING_GUARD_COPY: Record<
  MeetingGuardAction,
  { description: string; stopErrorMessage: string }
> = {
  quit: {
    description:
      'A meeting is currently being transcribed. Stop transcribing before quitting.',
    stopErrorMessage: 'Failed to stop meeting before quit',
  },
  logout: {
    description:
      'A meeting is currently being transcribed. Stop transcribing before signing out.',
    stopErrorMessage: 'Failed to stop meeting before logout',
  },
};

function showMeetingGuardConfirmation(action: MeetingGuardAction) {
  if (!activeMeetingGuardConfirmation) {
    activeMeetingGuardConfirmation = ModalsManager.showResolvable(
      ConfirmDialog,
      {
        title: 'Meeting in Progress',
        description: MEETING_GUARD_COPY[action].description,
        confirmText: 'Stop Transcribing',
        confirmVariant: 'destructive',
      },
      { unique: true }
    ).finally(() => {
      activeMeetingGuardConfirmation = null;
    });
  }

  return activeMeetingGuardConfirmation;
}

function requestMeetingStop(action: MeetingGuardAction) {
  if (activeMeetingStopRequest) {
    return;
  }

  activeMeetingStopRequest = meetingApis
    .enqueueStopMeeting({ type: 'stop_note_taking' })
    .catch((error) => {
      console.error(MEETING_GUARD_COPY[action].stopErrorMessage, error);
    })
    .finally(() => {
      activeMeetingStopRequest = null;
    });
}

export async function ensureMeetingNotActiveForAction(
  action: MeetingGuardAction
): Promise<MeetingGuardResult> {
  if (!meetingStore.asRecordingOrStoppingMeeting) {
    await electronApis.meetings.clearStaleMeetingState().catch(() => {});
    return 'allowed';
  }

  const confirmed = await showMeetingGuardConfirmation(action);

  if (confirmed) {
    requestMeetingStop(action);
  }

  return 'blocked';
}

export async function logoutWithMeetingGuard(reason: LogoutReason = 'manual') {
  const guardResult = await ensureMeetingNotActiveForAction('logout');
  if (guardResult === 'blocked') {
    return;
  }

  await waitForPostProcessingFinalization();
  await logoutUser(reason);
}
